import React, { useState, useEffect } from 'react';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import { Link } from 'react-router-dom';
import { getMealPlanner } from 'redux/modules/meal-planner/selectors';
import classnames from 'classnames';
import { LinkAsButton } from '@johnlewispartnership/wtr-ingredients/ingredients/LinkAsButton/LinkAsButton';
import { Checkbox } from '@johnlewispartnership/wtr-ingredients/ingredients/Checkbox/Checkbox';
import { LabeledControl } from '@johnlewispartnership/wtr-ingredients/ingredients/LabeledControl/LabeledControl';
import { Helmet } from 'react-helmet-async';
import root from 'window-or-global';
import { dataLayer } from 'analytics/data-layer';
import { emptyFilter } from 'components/Recipes/MealPlanner/utils/filter';
import { MEAL_PLANNER_CLEAR } from 'redux/modules/meal-planner/actions/types';
import { getURLParamValue } from '../../../redux/modules/recipes/utils/get-url-param-value';
import styles from './Step-2.scss';
import plannerStyles from './index.scss';
import step1Styles from './Step-1.scss';

const { history } = root;

export const Step2 = () => {
  const dispatch = useWtrDispatch();

  const { filters } = useWtrSelector(getMealPlanner);
  const dietary = filters.find(filter => filter.title === 'Dietary') || emptyFilter;
  const initialSelectedDietaryRequirements = history?.state?.dietaryRequirements || [];
  const servingSize = history?.state?.servingSize || getURLParamValue('Serving Size');

  const [selectedDietaryRequirements, setSelectedDietaryRequirements] = useState<string[]>(
    initialSelectedDietaryRequirements,
  );

  useEffect(() => {
    if (selectedDietaryRequirements.length > 0) {
      history.replaceState({ dietaryRequirements: selectedDietaryRequirements }, '');
    }
  }, [selectedDietaryRequirements]);

  const handleCheckboxChange = (dietaryRequirement: string) => {
    setSelectedDietaryRequirements(prevSelected =>
      prevSelected.includes(dietaryRequirement)
        ? prevSelected.filter(req => req !== dietaryRequirement)
        : [...prevSelected, dietaryRequirement],
    );

    dispatch({ type: MEAL_PLANNER_CLEAR });
  };

  return (
    <>
      <Helmet meta={[{ name: 'robots', content: 'noindex' }]} />
      <div className={classnames(plannerStyles.content, step1Styles.content, styles.content)}>
        <Typography component="span" styleAs="paragraphHeading" className={step1Styles.stepHeading}>
          Step 2 of 2
        </Typography>
        <Typography component="h1" styleAs="screenHeadingSmall" className={styles.stepTitle}>
          Dietary requirements
        </Typography>
        <Typography styleAs="paragraph" className={styles.stepDescription}>
          You can select multiple options or none as required.
        </Typography>

        {dietary.values.map(dietaryRequirement => (
          <LabeledControl
            className={step1Styles.label}
            name="dietaryRequirements"
            key={dietaryRequirement.name}
            control={
              <Checkbox
                name={dietaryRequirement.name}
                checked={selectedDietaryRequirements.includes(dietaryRequirement.name)}
                onChange={() => handleCheckboxChange(dietaryRequirement.name)}
              />
            }
            label={dietaryRequirement.name}
          />
        ))}
        <LinkAsButton
          to={`../builder?Serving Size=${encodeURIComponent(
            servingSize,
          )}&Dietary=${encodeURIComponent(selectedDietaryRequirements.join(','))}`}
          component={Link}
          /* @ts-expect-error finalising prop */
          theme="finalising"
          onClick={() => {
            dataLayer.push({
              event: 'complete_dietary_requirements',
              dietary_requirements: selectedDietaryRequirements.join('|'),
            });
          }}
          className={plannerStyles.nextButton}
        >
          Next
        </LinkAsButton>
      </div>
    </>
  );
};

export default Step2;
